import { useContext, useEffect, useState } from "react";
import {
  Button, Container, Grid, InputBase, Stack, IconButton, Typography,
  Select, Box, MenuItem, Table, TableHead, TableRow, TableBody,
  TableContainer, Paper, Pagination, Backdrop, CircularProgress,
  InputLabel, FormControl, ThemeProvider, createTheme, TextField,
  InputAdornment, Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import axios, { AxiosInstance } from "axios";
import Cookies from "universal-cookie";
import OptionalWidthModal from "../../../../commons/optional-width-modal/OptionalWidthModal";
import { AlertType } from "../../../../types/AlertType";
import { SnackBarAlert } from "../../../element/SnackBarAlert";
import CheckAttr from "../../../../utils/CheckAttr";
import { StyledTableCell, tableStyle } from "../Pays/Pays";
import { urlApi } from "../Pays/RowPays";
import { DataRef } from "../../../../types/DataRef";
import SecteurActiviteRow from "./SecteurActiviteRow";
import SecteurActiviteForms from "../../../forms/secteurActivite/SecteurActiviteForms";
import { loggoutUser } from "../../../../redux/actions/logginUser";
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { MediaQueryContext } from "../../../../utils/contextes/MediaQueryContext";

const SecteurActivite = () => {
  const { isDesktopOrLaptop } = useContext(MediaQueryContext);

  const [allSecteurActivite, setAllSecteurActivite] = useState<DataRef[]>([]);
  const [libelleSearch, setLibelleSearch] = useState<string>("");
  const [loader, setLoader] = useState<boolean>(false);
  const [rowPerPage, setRowPerPage] = useState(5);
  const [page, setPage] = useState<number>(1);
  const [openSnack, setOpenSnack] = useState(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [nb, setNb] = useState(0);
  const [openModal, setOpenModal] = useState<boolean>(false);

  const [alertMessage, setAlerteMessage] = useState<string>("");
  const [alertType, setAlertType] = useState<AlertType>("success");

  const getDataSecteurActivite = async () => {
    setLoader(true);
    setAllSecteurActivite([]);
    setNb(0);
    await http
      .post(`secteuract/list`, {
        rowPerPage,
        page,
        libelleSearch,
      })
      .then((res) => {
        const { data, total } = res.data;
        setAllSecteurActivite(data);
        setNb(total);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const theme = createTheme({
    palette: {
      primary: {
        main: "#FC8404",
        contrastText: "#EEE",
      },
      secondary: {
        main: "#EEE",
      },
    },
  });

  const cookies = new Cookies();
  const token: string = cookies.get("jwt");

  const http: AxiosInstance = axios.create({
    baseURL: urlApi,
    // timeout: 1000,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Add global response interceptor
  http.interceptors.response.use(
    (response) => {
      // Modify response data here, if needed
      return response;
    },
    (error) => {
      console.log(error.message);
      if (error.message === "Request failed with status code 401") {
        // Remove the redux store
        dispatch(loggoutUser());

        // Remove token in navigator cookies
        cookies.remove('jwt', { path: '/' });

        navigate("/sign-in");
      }
      return Promise.reject(error);
    }
  );


  const stackAlert: JSX.Element = SnackBarAlert(
    openSnack,
    () => {
      setOpenSnack(false);
    },
    alertType,
    alertMessage
  );

  const ClickSearch = () => {
    setPage(1);
    getDataSecteurActivite();
  };

  useEffect(() => {
    getDataSecteurActivite();
  }, [page, rowPerPage, refresh]);

  const handleReload = () => {
    setRefresh(!refresh);
  };

  const handleChangeSearch = (e: any) => {
    const { value } = e.target;

    if (CheckAttr(value)) {
      setLibelleSearch(value);
    } else {
      reloadList();
    }
  };

  const handleChangeRowPerPage = (e: any) => {
    setPage(1);
    setRowPerPage(e.target.value);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const reloadList = () => {
    setPage(1);
    setRowPerPage(5);
    setLibelleSearch('');
    setRefresh(!refresh);
  };

  const pagination: JSX.Element | boolean = !loader && (
    <Stack spacing={2} direction="row">
      <InputLabel sx={{ color: "#fff" }}>Lignes par page :</InputLabel>
      <FormControl variant="standard" sx={{ minWidth: 50 }}>
        <Select
          labelId="demo-simple-select-standard-label"
          id="demo-simple-select-standard"
          color="warning"
          sx={{ background: "#fff", borderRadius: 1, paddingLeft: 1 }}
          value={rowPerPage}
          onChange={handleChangeRowPerPage}
          defaultValue={5}
        >
          <MenuItem value={5}>5</MenuItem>
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={25}>25</MenuItem>
          <MenuItem value={50}>50</MenuItem>
        </Select>
      </FormControl>
      {nb > rowPerPage && (
        <ThemeProvider theme={theme}>
          <Pagination
            count={Math.ceil(nb / rowPerPage)}
            color="standard"
            sx={{ button: { color: "white" } }}
            showFirstButton={true}
            showLastButton={true}
            siblingCount={0}
            boundaryCount={1}
            defaultPage={page}
            onChange={(event, value) => setPage(value)}
          />
        </ThemeProvider>
      )}
    </Stack>
  );

  return (
    <>
      <Container maxWidth="lg">
        {isDesktopOrLaptop ? (
          <>
            <Grid container columns={{ xs: 6, sm: 12, md: 12 }}>
              <Grid item xs={2} sm={7} md={8}>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenModal(true)}
                  style={{ textTransform: "none" }}
                >
                  Ajouter
                </Button>
              </Grid>
              <Grid item xs={4} sm={5} md={4}>
                <Typography sx={{ display: "flex" }}>
                  <InputBase
                    sx={{ ml: 4, flex: 1, color: "white" }}
                    value={libelleSearch}
                    placeholder="Chercher un Secteur ..."
                    onChange={handleChangeSearch}
                    fullWidth
                  />
                  <Grid item xs={2}>
                    <IconButton
                      type="submit"
                      sx={{ p: "10px", color: "white" }}
                      aria-label="search"
                      onClick={() => ClickSearch()}
                    >
                      <SearchIcon />
                    </IconButton>
                  </Grid>
                </Typography>
              </Grid>
            </Grid>

            <Box display='flex' justifyContent='start' mt={4}>
              {pagination}
            </Box>

            <TableContainer component={Paper} style={tableStyle}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="center">
                      Secteur Activite
                    </StyledTableCell>
                    <StyledTableCell align="center">Action</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allSecteurActivite.map((row) => (
                    <SecteurActiviteRow
                      row={row}
                      http={http}
                      isDesktopOrLaptop={isDesktopOrLaptop}
                      reloadList={handleReload}
                      onClose={handleCloseModal}
                      setAlertType={setAlertType}
                      setAlerteMessage={setAlerteMessage}
                      setOpenSnack={setOpenSnack}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box display="flex" justifyContent="end" mt={4}>
              {pagination}
            </Box>
          </>
        ) : (
          <>
            <Box sx={{
              position: 'fixed',
              width: '95%',
              top: '10.32rem',
              pr: 1.5,
              pt: 2,
              bgcolor: '#080d24',
              zIndex: 1000
            }}>
              <Button
                variant="contained"
                size='small'
                startIcon={<AddIcon />}
                color="warning"
                fullWidth
                sx={{ textTransform: "none" }}
                onClick={() => setOpenModal(true)}
              >
                Ajouter
              </Button>

              <TextField
                id="outlined-basic"
                variant="outlined"
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 1,
                  mt: 1,
                  mb: 4
                }}
                placeholder="Chercher un Secteur ..."
                size="small"
                fullWidth
                value={libelleSearch}
                onChange={handleChangeSearch}
                color="warning"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        aria-label="search"
                        onClick={() => ClickSearch()}
                      >
                        <SearchIcon />
                      </IconButton>
                      <IconButton
                        aria-label="refresh"
                        onClick={() => reloadList()}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Typography>Liste Secteurs d'activités</Typography>
              <Divider orientation="horizontal" sx={{ bgcolor: "#fff", mb: 3 }} />

              {!loader && nb > rowPerPage && (
                <Box display="flex" justifyContent="end">
                  <Pagination
                    count={Math.ceil(nb / rowPerPage)}
                    size="small"
                    color="standard"
                    sx={{ button: { color: "white" }, mb: 2 }}
                    showFirstButton={true}
                    showLastButton={true}
                    siblingCount={0}
                    boundaryCount={1}
                    defaultPage={page}
                    onChange={(event, value) => setPage(value)}
                  />
                </Box>
              )}
            </Box>

            <Box mt='12.5rem'>
              {allSecteurActivite.map((row) => (
                <SecteurActiviteRow
                  key={row?.id}
                  row={row}
                  http={http}
                  isDesktopOrLaptop={isDesktopOrLaptop}
                  reloadList={handleReload}
                  onClose={handleCloseModal}
                  setAlertType={setAlertType}
                  setAlerteMessage={setAlerteMessage}
                  setOpenSnack={setOpenSnack}
                />
              ))}
            </Box>

            <Stack direction="row" sx={{ mt: 3, mb: 4 }}>
              <InputLabel sx={{ color: "#fff", fontSize: 12, mt: 0.5 }}>
                Lignes par page :
              </InputLabel>
              <FormControl variant="standard" sx={{ minWidth: 48 }}>
                <Select
                  labelId="demo-simple-select-standard-label"
                  id="demo-simple-select-standard"
                  color="warning"
                  sx={{
                    background: "#fff",
                    borderRadius: 1,
                    paddingLeft: 1,
                    paddingTop: 0.5,
                    marginLeft: 1,
                    fontSize: 12,
                  }}
                  value={rowPerPage}
                  onChange={(e) => setRowPerPage(e.target.value as number)}
                  defaultValue={5}
                  size="small"
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </>
        )}
      </Container>

      <div>
        <Backdrop
          sx={{ color: "white", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>

      <OptionalWidthModal
        title="Ajouter un Secteur D'activite"
        // breackPoint="lg"
        onClose={handleCloseModal}
        open={openModal}
      >
        <SecteurActiviteForms
          onClose={handleCloseModal}
          reloadList={reloadList}
          setAlertType={setAlertType}
          setAlerteMessage={setAlerteMessage}
          setOpenSnack={setOpenSnack}
          http={http}
        />
      </OptionalWidthModal>
      {stackAlert}
    </>
  );
};

export default SecteurActivite;
